import { useCallback, useMemo } from "react";

import { useTransitionBlock } from "@/hooks/use-transition-block";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { Button } from "@/components/ui/button/button";

import { useUpdateCustomMailSetting } from "../../api";

import type { BasicCustomMailSettingFormData } from "../../schema";
import type { ComponentProps } from "react";
import type { UseFormReturn } from "react-hook-form";

type Props = {
	form: UseFormReturn<BasicCustomMailSettingFormData>;
};

export const HeaderRightButton = ({ form }: Props) => {
	const { formState, getValues, reset } = form;
	const { isValid, isDirty } = formState;
	const { updateCustomMailSetting } = useUpdateCustomMailSetting();
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	useTransitionBlock(isDirty);

	const buttonVariant = useMemo<ComponentProps<typeof Button>["variant"]>(() => {
		if (!isValid || !isDirty) {
			return "disabled";
		}
		return "default";
	}, [isValid, isDirty]);

	const handleClick = useCallback(async () => {
		if (!isValid || !isDirty) return;

		showLoadingScreenCallback();
		try {
			const formData = getValues();

			const { data, errors } = await updateCustomMailSetting(formData);

			if (!data || errors?.length) {
				notifyUserEventInfo({
					variant: "error",
					title: "設定の保存に失敗しました",
					description: "お手数をおかけしますが、時間を置いてから再度お試しください。",
				});
				return;
			}

			notifyUserEventInfo({
				title: "設定を保存しました",
				description: "メール設定が正常に更新されました。",
			});

			reset(formData);
		} catch (error) {
			notifyUserEventInfo({
				variant: "error",
				title: "設定の保存に失敗しました",
				description: "ネットワークエラーが発生しました。再度お試しください。",
			});
		} finally {
			hideLoadingScreenCallback();
		}
	}, [
		getValues,
		isValid,
		isDirty,
		updateCustomMailSetting,
		showLoadingScreenCallback,
		hideLoadingScreenCallback,
		reset,
	]);

	return (
		<Button size="xl" variant={buttonVariant} onClick={handleClick}>
			保存
		</Button>
	);
};

HeaderRightButton.displayName = "HeaderRightButton";
